import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  productApi,
  type ProductConfig,
  type ProductEdition,
  type ProductId,
} from '../lib/api';
import { applyProductDocumentMetadata } from '../lib/productMetadata';
import {
  productSurfaceFromRouterPath,
  resolveAvailableProductSurface,
  selectAvailableProductSurface,
} from '../lib/productSurface';

const ACTIVE_PRODUCT_KEY = 'boss_active_product';

function editionFromBuild(): ProductEdition {
  const meta = import.meta as ImportMeta & { env?: Record<string, string | undefined> };
  const value = String(meta.env?.VITE_PRODUCT_EDITION ?? '').toLowerCase();
  if (value === 'hos' || value === 'both') return value;
  return 'bos';
}

function productsForEdition(edition: ProductEdition): ProductId[] {
  return edition === 'both' ? ['bos', 'hos'] : [edition];
}

function fallbackConfig(): ProductConfig {
  const edition = editionFromBuild();
  return {
    edition,
    defaultProduct: edition === 'hos' ? 'hos' : 'bos',
    bosName: '',
    hosName: '',
    eaName: '',
    paName: '',
    modules: [],
    integrations: edition === 'hos' ? ['whatsapp'] : edition === 'both' ? ['linkedin', 'whatsapp'] : [],
    configured: false,
  };
}

function normaliseConfig(value: ProductConfig): ProductConfig {
  const edition: ProductEdition = value.edition === 'hos' || value.edition === 'both' ? value.edition : 'bos';
  const products = productsForEdition(edition);
  const defaultProduct = products.includes(value.defaultProduct) ? value.defaultProduct : products[0];
  return {
    edition,
    defaultProduct,
    bosName: typeof value.bosName === 'string' ? value.bosName.trim() : '',
    hosName: typeof value.hosName === 'string' ? value.hosName.trim() : '',
    eaName: typeof value.eaName === 'string' ? value.eaName.trim() : '',
    paName: typeof value.paName === 'string' ? value.paName.trim() : '',
    modules: Array.isArray(value.modules)
      ? [...new Set(value.modules.filter((item): item is string => typeof item === 'string').map((item) => item.trim()).filter(Boolean))]
      : [],
    integrations: Array.isArray(value.integrations)
      ? [...new Set(value.integrations.filter((item): item is string => typeof item === 'string').map((item) => item.trim().toLowerCase()).filter(Boolean))]
      : [],
    configured: value.configured === true,
  };
}

function readStoredProduct(): ProductId | null {
  try {
    const value = localStorage.getItem(ACTIVE_PRODUCT_KEY);
    return value === 'bos' || value === 'hos' ? value : null;
  } catch {
    return null;
  }
}

interface ProductContextValue {
  config: ProductConfig;
  activeProduct: ProductId;
  availableProducts: ProductId[];
  productName: string;
  assistantName: string;
  loading: boolean;
  usingFallback: boolean;
  error: string | null;
  setActiveProduct: (product: ProductId) => void;
  moduleEnabled: (moduleId: string) => boolean;
  integrationEnabled: (integrationId: string) => boolean;
  refresh: () => Promise<void>;
  updateConfig: (patch: Partial<ProductConfig>) => Promise<ProductConfig>;
}

const ProductContext = createContext<ProductContextValue | null>(null);

export function ProductProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [config, setConfig] = useState<ProductConfig>(fallbackConfig);
  const [activeProduct, setActiveProductState] = useState<ProductId>(() => {
    const fallback = fallbackConfig();
    const stored = readStoredProduct();
    return stored && productsForEdition(fallback.edition).includes(stored) ? stored : fallback.defaultProduct;
  });
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const availableProducts = useMemo(() => productsForEdition(config.edition), [config.edition]);

  const applyConfig = useCallback((nextValue: ProductConfig) => {
    const next = normaliseConfig(nextValue);
    const products = productsForEdition(next.edition);
    const selected = selectAvailableProductSurface(readStoredProduct(), next.defaultProduct, products);
    setConfig(next);
    setActiveProductState(selected);
    try { localStorage.setItem(ACTIVE_PRODUCT_KEY, selected); } catch { /* no storage */ }
    return next;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      applyConfig(await productApi.getConfig());
      setUsingFallback(false);
      setError(null);
    } catch (cause) {
      applyConfig(fallbackConfig());
      setUsingFallback(true);
      setError(cause instanceof Error ? cause.message : 'Product configuration is unavailable');
    } finally {
      setLoading(false);
    }
  }, [applyConfig]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const routed = productSurfaceFromRouterPath(location.pathname);
    if (!routed || !availableProducts.includes(routed)) return;
    setActiveProductState(routed);
    try { localStorage.setItem(ACTIVE_PRODUCT_KEY, routed); } catch { /* no storage */ }
  }, [availableProducts, location.pathname]);

  const setActiveProduct = useCallback((product: ProductId) => {
    if (!productsForEdition(config.edition).includes(product)) return;
    setActiveProductState(product);
    try { localStorage.setItem(ACTIVE_PRODUCT_KEY, product); } catch { /* no storage */ }
  }, [config.edition]);

  const updateConfig = useCallback(async (patch: Partial<ProductConfig>) => {
    const next = applyConfig(await productApi.updateConfig(patch));
    setUsingFallback(false);
    setError(null);
    return next;
  }, [applyConfig]);

  const moduleEnabled = useCallback((moduleId: string) => {
    const wanted = moduleId.trim().toLowerCase();
    return config.modules.some((item) => item.toLowerCase() === wanted);
  }, [config.modules]);

  const integrationEnabled = useCallback((integrationId: string) => {
    const wanted = integrationId.trim().toLowerCase();
    return config.integrations.some((item) => item.toLowerCase() === wanted);
  }, [config.integrations]);

  const routedProduct = productSurfaceFromRouterPath(location.pathname);
  const resolvedProduct = resolveAvailableProductSurface(routedProduct, activeProduct, availableProducts);
  const productName = resolvedProduct === 'hos' ? (config.hosName || 'P-HAS') : (config.bosName || 'BOS');
  const assistantName = resolvedProduct === 'hos'
    ? (config.paName || 'Personal Assistant')
    : (config.eaName || 'Executive Assistant');

  useEffect(() => {
    applyProductDocumentMetadata(document, resolvedProduct, productName);
  }, [productName, resolvedProduct]);

  const value = useMemo<ProductContextValue>(() => ({
    config,
    activeProduct: resolvedProduct,
    availableProducts,
    productName,
    assistantName,
    loading,
    usingFallback,
    error,
    setActiveProduct,
    moduleEnabled,
    integrationEnabled,
    refresh,
    updateConfig,
  }), [
    assistantName,
    availableProducts,
    config,
    error,
    loading,
    moduleEnabled,
    integrationEnabled,
    productName,
    refresh,
    resolvedProduct,
    setActiveProduct,
    updateConfig,
    usingFallback,
  ]);

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProduct(): ProductContextValue {
  const value = useContext(ProductContext);
  if (!value) throw new Error('useProduct must be used inside ProductProvider');
  return value;
}
